import { useCallback } from 'react'

import { useListBrandTokensQuery } from './useBrandTokens'

const useBrandColor = () => {
  const { data: brandColors } = useListBrandTokensQuery('color')

  return useCallback(
    (colorKey: string | undefined) => {
      if (!colorKey) return undefined
      if (!colorKey.startsWith('brand:')) return colorKey

      const brandColor = brandColors?.find(
        (c) => c.key === colorKey.substring('brand:'.length)
      )

      if (brandColor) return brandColor.value
      return undefined
    },
    [brandColors]
  )
}

const useBrandGraphic = () => {
  const { data: graphics } = useListBrandTokensQuery('graphic')

  return useCallback(
    (graphicUrl: string | undefined) => {
      if (!graphicUrl) return undefined
      if (!graphicUrl.startsWith('brand:')) return graphicUrl

      const graphic = graphics?.find(
        (g) => g.key === graphicUrl.substring('brand:'.length)
      )

      if (graphic) return graphic.value
      return undefined
    },
    [graphics]
  )
}

const useBrandTypography = () => {
  const { data: brandTypographyList } = useListBrandTokensQuery('typography')

  return useCallback(
    (typographyKey: string | undefined) => {
      if (!typographyKey) return undefined
      if (!typographyKey.startsWith('brand:')) return typographyKey

      const brandTypography = brandTypographyList?.find(
        (c) => c.key === typographyKey.substring('brand:'.length)
      )

      if (brandTypography) return brandTypography.string
      return undefined
    },
    [brandTypographyList]
  )
}

type BrandTypography = {
  family: string
  fallback?: string
  weight?: number
  size: number
  lineHeight?: number
  style?: string
}

const DEFAULT_TYPOGRAPHY = {
  family: 'Inter',
  fallback: 'sans-serif',
  weight: 400,
  size: 12,
  lineHeight: 1.5,
  style: 'normal',
} satisfies BrandTypography

const parseTypography = (
  fontShorthand: string | undefined
): BrandTypography => {
  if (!fontShorthand) {
    return DEFAULT_TYPOGRAPHY
  }
  const regex =
    /(?:(?<style>italic|normal|oblique)\s)?(?:(?<weight>\d{3})\s)?((?<size>\d+)px)?(?:\/(?<lineHeight>\d+(?:\.\d+)?))?\s+"(?<family>.+)"(?:,(?<fallback>[\w\s,-]+))?/
  const match = fontShorthand.match(regex)

  if (!match?.groups) {
    return DEFAULT_TYPOGRAPHY
  }

  const { style, weight, size, lineHeight, family, fallback } = match.groups
  return {
    family,
    fallback,
    style,
    size: parseInt(size, 10),
    weight: weight ? parseInt(weight, 10) : undefined,
    lineHeight: lineHeight ? parseInt(lineHeight, 10) : undefined,
  }
}

export {
  useBrandColor,
  useBrandGraphic,
  useBrandTypography,
  parseTypography,
  type BrandTypography,
}
